import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import {
  companionKeys,
  COMPANION_PATH,
  invalidateCompanionQueries,
} from '@/hooks/queries/companion-keys';
import api from '@/libs/axiosInstance';
import {
  ErrorCode,
  type ApiResponse,
  type CompanionActionDto,
  type CompanionActionMutationDto,
  type CompanionDismissReason,
  type CompanionFeedDto,
  type CompanionPanelViewDto,
} from '@fluenceur/types';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Everything the panel writes back: the opening of the panel itself, the
 * exposure of a card, the answer a brand gives one and the taking back of that
 * answer. Opening a card is not here — it belongs to the navigation hook, which
 * must record the opening and the destination as one gesture.
 *
 * Every call echoes the version it saw. When the server refuses a stale echo it
 * hands back the state that refused it, and this hook redraws from that answer
 * instead of racing the same stale version back.
 */

type CompanionConflictPayload = { action?: CompanionActionDto; feed?: CompanionFeedDto };
type CompanionApiError = AxiosError<ApiResponse<CompanionConflictPayload>>;

/** The refusals that mean "your view is behind", not "something broke". */
const CONFLICT_CODES: ReadonlySet<string> = new Set([
  ErrorCode.E_COMPANION_VERSION_CONFLICT,
  ErrorCode.E_COMPANION_UNDO_EXPIRED,
  ErrorCode.E_COMPANION_UNDO_CONFLICT,
]);

/** Puts the card the server just described back into the cached feed. */
const replaceActionInFeed = (
  queryClient: QueryClient,
  brandId: string,
  action: CompanionActionDto
): void => {
  queryClient.setQueryData<CompanionFeedDto>(companionKeys.brand(brandId), (feed) => {
    if (!feed) return feed;
    return {
      ...feed,
      actions: feed.actions.map((current) => (current.id === action.id ? action : current)),
    };
  });
};

export const useCompanionActions = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  /**
   * A refused mutation is answered with a redraw, never with a retry: the
   * gesture was made against a card that no longer exists as it was, and
   * replaying it would apply an answer to something else.
   */
  const handleError = useCallback(
    (error: CompanionApiError) => {
      const payload = error.response?.data;
      const code = payload?.code;

      if (code && CONFLICT_CODES.has(code)) {
        if (payload?.data?.feed) {
          queryClient.setQueryData(companionKeys.brand(brandId), payload.data.feed);
        } else if (payload?.data?.action) {
          replaceActionInFeed(queryClient, brandId, payload.data.action);
        }
        toaster.create({ type: 'info', description: t('companion.errors.changed') });
      } else {
        toaster.error({
          title: t('common.error'),
          description: payload?.message ?? t('common.error'),
        });
      }

      invalidateCompanionQueries(queryClient, brandId);
    },
    [brandId, queryClient, t]
  );

  const settle = useCallback(() => {
    invalidateCompanionQueries(queryClient, brandId);
  }, [brandId, queryClient]);

  const panelViewMutation = useMutation<CompanionPanelViewDto | null, CompanionApiError, void>({
    mutationFn: async () => {
      const { data } = await api.post<ApiResponse<CompanionPanelViewDto>>(
        `${COMPANION_PATH}/feed/view`
      );
      return data.data ?? null;
    },
    // Recording an opening changes nothing a reader can see, and failing to
    // record one must never interrupt the reading.
    onError: () => {},
  });

  const seenMutation = useMutation<void, CompanionApiError, { actionId: string; version: number }>({
    mutationFn: async ({ actionId, version }) => {
      await api.post(`${COMPANION_PATH}/actions/${actionId}/seen`, { version });
    },
    // No refetch: a read receipt writes an event, leaves the card untouched and
    // deliberately does not bump its version, so the feed that would come back
    // is the one already on screen. Refetching it would only cost a round trip
    // per card the reader flips through.
    // An exposure the server refused is an exposure the reader still had. It is
    // not worth a message; the next refetch fixes the view.
    onError: () => {},
  });

  const dismissMutation = useMutation<
    CompanionActionMutationDto,
    CompanionApiError,
    { action: CompanionActionDto; reason: CompanionDismissReason }
  >({
    mutationFn: async ({ action, reason }) => {
      return trackProductAction('companion', 'decline', async () => {
        const { data } = await api.post<ApiResponse<CompanionActionMutationDto>>(
          `${COMPANION_PATH}/actions/${action.id}/dismiss`,
          { reason, version: action.version }
        );
        if (!data.data) throw new Error('Empty companion dismiss response');
        return data.data;
      });
    },
    onSuccess: settle,
    onError: handleError,
  });

  const undoMutation = useMutation<
    CompanionActionMutationDto,
    CompanionApiError,
    { actionId: string; version: number; eventId: string }
  >({
    mutationFn: async ({ actionId, version, eventId }) => {
      return trackProductAction('companion', 'update', async () => {
        const { data } = await api.post<ApiResponse<CompanionActionMutationDto>>(
          `${COMPANION_PATH}/actions/${actionId}/undo`,
          { eventId, version }
        );
        if (!data.data) throw new Error('Empty companion undo response');
        return data.data;
      });
    },
    onSuccess: settle,
    onError: handleError,
  });

  const recordPanelView = useCallback(() => {
    panelViewMutation.mutate();
  }, [panelViewMutation]);

  const markSeen = useCallback(
    (actionId: string, version: number) => {
      seenMutation.mutate({ actionId, version });
    },
    [seenMutation]
  );

  const dismiss = useCallback(
    async (input: { action: CompanionActionDto; reason: CompanionDismissReason }) => {
      try {
        return await dismissMutation.mutateAsync(input);
      } catch {
        return null;
      }
    },
    [dismissMutation]
  );

  const undo = useCallback(
    async (input: { actionId: string; version: number; eventId: string }) => {
      try {
        await undoMutation.mutateAsync(input);
        return true;
      } catch {
        return false;
      }
    },
    [undoMutation]
  );

  return {
    recordPanelView,
    markSeen,
    dismiss,
    undo,
    isDismissing: dismissMutation.isPending,
    isUndoing: undoMutation.isPending,
  };
};
